import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { credentialingService } from '../../../services/credentialingService';

const CredentialModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    credentialType: '',
    credentialNumber: '',
    issuingAuthority: '',
    issueDate: '',
    expirationDate: '',
    specialty: '',
    state: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const credentialTypes = [
    { value: 'dental_license', label: 'Dental License' },
    { value: 'dea', label: 'DEA Registration' },
    { value: 'cds', label: 'CDS Registration' },
    { value: 'specialty_certification', label: 'Specialty Certification' },
    { value: 'cpr_bls', label: 'CPR/BLS Certification' },
    { value: 'radiology', label: 'Radiology Certification' },
    { value: 'sedation_permit', label: 'Sedation Permit' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.credentialType || !formData?.credentialNumber || !formData?.issuingAuthority || !formData?.issueDate || !formData?.expirationDate) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await credentialingService?.createCredential(formData);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to create credential');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-background p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
              <Icon name="Award" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Add New Credential</h2>
              <p className="text-sm text-muted-foreground mt-1">Enter credential details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground transition-smooth hover:bg-muted hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive rounded-lg p-3">
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Credential Type <span className="text-destructive">*</span>
            </label>
            <Select
              options={credentialTypes}
              value={formData?.credentialType}
              onChange={(value) => handleChange('credentialType', value)}
              placeholder="Select credential type"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Credential Number"
              required
              value={formData?.credentialNumber}
              onChange={(e) => handleChange('credentialNumber', e?.target?.value)}
              placeholder="e.g., DL-2024-12345"
            />
            <Input
              label="State"
              value={formData?.state}
              onChange={(e) => handleChange('state', e?.target?.value)}
              placeholder="e.g., CA"
            />
          </div>

          <Input
            label="Issuing Authority"
            required
            value={formData?.issuingAuthority}
            onChange={(e) => handleChange('issuingAuthority', e?.target?.value)}
            placeholder="e.g., State Dental Board"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Issue Date"
              type="date"
              required
              value={formData?.issueDate}
              onChange={(e) => handleChange('issueDate', e?.target?.value)}
            />
            <Input
              label="Expiration Date"
              type="date"
              required
              value={formData?.expirationDate}
              onChange={(e) => handleChange('expirationDate', e?.target?.value)}
            />
          </div>

          <Input
            label="Specialty"
            value={formData?.specialty}
            onChange={(e) => handleChange('specialty', e?.target?.value)}
            placeholder="e.g., General Dentistry"
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
              placeholder="Additional notes..."
              rows={3}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={loading}
              iconName="Plus"
              iconPosition="left"
            >
              Add Credential
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CredentialModal;